import { AnimalDocumentModel } from '../../models/animal-document.model';
import { AnimalMediaModel } from '../../models/animal-media.model';
import { AnimalMedicalConditionModel } from '../../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordModel } from '../../models/medical/animal-medical-record.model';
import { AnimalPreventiveTreatmentModel } from '../../models/medical/animal-preventive-treatment.model';
import { AnimalQuarantineModel } from '../../models/medical/animal-quarantine.model';
import { AnimalVeterinaryContactModel } from '../../models/medical/animal-veterinary-contact.model';
import { PreventiveTreatmentCategory, PreventiveTreatmentType } from '../../models/medical/medical.enums';
import { isoDatePart } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';

export interface MedicalRecordPositions {
  treatments: AnimalPreventiveTreatmentModel[];
  conditions: AnimalMedicalConditionModel[];
}

export type MedicalFileKind = 'document' | 'image' | 'video';

/** Einheitliche Sicht auf Dokumente, Bilder und Videos, die an einer Karteikarte hängen. */
export interface MedicalFile {
  key: string;
  kind: MedicalFileKind;
  id: number;
  recordId: number;
  name: string;
  /** Dokumentkategorie bzw. `image`/`video`. */
  category: string;
  date: string | null;
  size: number | null;
  document?: AnimalDocumentModel;
  media?: AnimalMediaModel;
}

export interface TreatmentGroup {
  key: string;
  category: PreventiveTreatmentCategory;
  type: PreventiveTreatmentType | null;
  treatment: AnimalPreventiveTreatmentModel;
}

export interface MedicalCounts {
  records: number;
  prophylaxis: number;
  prophylaxisAlerts: number;
  conditions: number;
  quarantines: number;
  attachments: number;
}

/** Behandlungen über `medicalRecord.id`, Diagnosen über `diagnosedByRecord.id`. */
export function positionsByRecordId(
  treatments: AnimalPreventiveTreatmentModel[],
  conditions: AnimalMedicalConditionModel[]
): Record<number, MedicalRecordPositions> {
  const result: Record<number, MedicalRecordPositions> = {};
  const bucket = (recordId: number) => (result[recordId] ??= { treatments: [], conditions: [] });

  for (const treatment of treatments) {
    const recordId = refId(treatment.medicalRecord);
    if (recordId !== null) bucket(recordId).treatments.push(treatment);
  }
  for (const condition of conditions) {
    const recordId = refId(condition.diagnosedByRecord);
    if (recordId !== null) bucket(recordId).conditions.push(condition);
  }
  return result;
}

function fileName(path: string | null | undefined, fallback: string): string {
  if (!path) return fallback;
  const clean = path.split('?')[0];
  return decodeURIComponent(clean.split('/').pop() || fallback);
}

/** Nur Dateien mit Karteikarten-Verknüpfung. */
export function toMedicalFiles(
  documents: AnimalDocumentModel[],
  images: AnimalMediaModel[],
  videos: AnimalMediaModel[]
): MedicalFile[] {
  const files: MedicalFile[] = [];

  for (const document of documents) {
    const recordId = refId(document.animalMedicalRecord);
    if (recordId === null) continue;
    files.push({
      key: `document-${document.id}`,
      kind: 'document',
      id: document.id,
      recordId,
      name: fileName(document.filePath, `document_${document.id}`),
      category: document.category,
      date: document.date ?? document.createdAt ?? null,
      size: null,
      document
    });
  }

  const pushMedia = (media: AnimalMediaModel, kind: 'image' | 'video') => {
    const recordId = refId(media.medicalRecord);
    if (recordId === null) return;
    files.push({
      key: `${kind}-${media.id}`,
      kind,
      id: media.id,
      recordId,
      name: fileName(media.contentUrl, `${kind}_${media.id}`),
      category: kind,
      date: media.date ?? media.createdAt ?? null,
      size: media.fileSize ?? null,
      media
    });
  };
  images.forEach(image => pushMedia(image, 'image'));
  videos.forEach(video => pushMedia(video, 'video'));

  return files;
}

export function filesByRecordId(files: MedicalFile[]): Record<number, MedicalFile[]> {
  const result: Record<number, MedicalFile[]> = {};
  for (const file of files) {
    (result[file.recordId] ??= []).push(file);
  }
  return result;
}

/** Alte Id → Id des korrigierenden Eintrags (bei mehreren der jüngste). */
export function correctedByMap(records: AnimalMedicalRecordModel[]): Record<number, number> {
  const result: Record<number, number> = {};
  for (const record of records) {
    const correctedId = refId(record.correctsEntry);
    if (correctedId === null) continue;
    if (result[correctedId] === undefined || record.id > result[correctedId]) {
      result[correctedId] = record.id;
    }
  }
  return result;
}

/** Aktuelle Fassung → alle früheren Fassungen ihrer Korrekturkette, jüngste zuerst. */
export function correctionHistoryByHeadId(records: AnimalMedicalRecordModel[]): Record<number, AnimalMedicalRecordModel[]> {
  const correctedBy = correctedByMap(records);
  const result: Record<number, AnimalMedicalRecordModel[]> = {};

  for (const record of records) {
    if (correctedBy[record.id] === undefined) continue;
    let headId = record.id;
    const seen = new Set<number>([headId]);
    while (correctedBy[headId] !== undefined && !seen.has(correctedBy[headId])) {
      headId = correctedBy[headId];
      seen.add(headId);
    }
    if (headId === record.id) continue;
    (result[headId] ??= []).push(record);
  }
  for (const history of Object.values(result)) {
    history.sort((a, b) => b.id - a.id);
  }
  return result;
}

export function activeConditions(conditions: AnimalMedicalConditionModel[]): AnimalMedicalConditionModel[] {
  return conditions.filter(condition => !condition.resolvedAt);
}

/** Aktive und geplante Quarantänen: Ende offen oder nicht vor heute. */
export function activeOrPlannedQuarantines(quarantines: AnimalQuarantineModel[], today: string): AnimalQuarantineModel[] {
  return quarantines.filter(quarantine => {
    const endsAt = isoDatePart(quarantine.endsAt);
    return endsAt === null || endsAt >= today;
  });
}

export function isQuarantineActive(quarantine: AnimalQuarantineModel, today: string): boolean {
  const startsAt = isoDatePart(quarantine.startsAt);
  const endsAt = isoDatePart(quarantine.endsAt);
  return !!startsAt && startsAt <= today && (endsAt === null || endsAt >= today);
}

export function isQuarantinePlanned(quarantine: AnimalQuarantineModel, today: string): boolean {
  const startsAt = isoDatePart(quarantine.startsAt);
  return !!startsAt && startsAt > today;
}

function isNewerTreatment(a: AnimalPreventiveTreatmentModel, b: AnimalPreventiveTreatmentModel): boolean {
  const dateA = isoDatePart(a.administeredAt) ?? '';
  const dateB = isoDatePart(b.administeredAt) ?? '';
  if (dateA !== dateB) return dateA > dateB;
  return a.id > b.id;
}

/**
 * Jüngste Behandlung je Kategorie und Erreger, wie der Fälligkeitsrechner des Backends:
 * Eine Kombinationsimpfung zählt für jeden ihrer Erreger, Behandlungen ohne Erreger bilden
 * je Kategorie eine eigene Gruppe.
 */
export function latestTreatmentPerGroup(treatments: AnimalPreventiveTreatmentModel[]): TreatmentGroup[] {
  const latest = new Map<string, TreatmentGroup>();

  for (const treatment of treatments) {
    const types: (PreventiveTreatmentType | null)[] = treatment.types?.length ? treatment.types : [null];
    for (const type of types) {
      const key = `${treatment.category}|${type ?? ''}`;
      const previous = latest.get(key);
      if (!previous || isNewerTreatment(treatment, previous.treatment)) {
        latest.set(key, { key, category: treatment.category, type, treatment });
      }
    }
  }

  return Array.from(latest.values());
}

/** Jüngste Behandlungen mit offener Fälligkeit, je Behandlung nur einmal (Kombinationsimpfung). */
export function treatmentsWithOpenDue(treatments: AnimalPreventiveTreatmentModel[]): AnimalPreventiveTreatmentModel[] {
  const unique = new Map<number, AnimalPreventiveTreatmentModel>();
  for (const group of latestTreatmentPerGroup(treatments)) {
    if (group.treatment.nextDueAt) unique.set(group.treatment.id, group.treatment);
  }
  return Array.from(unique.values());
}

export function primaryVeterinaryContact(contacts: AnimalVeterinaryContactModel[]): AnimalVeterinaryContactModel | null {
  return contacts.find(contact => contact.isPrimary) ?? null;
}

export function medicalCounts(input: {
  records: AnimalMedicalRecordModel[];
  treatments: AnimalPreventiveTreatmentModel[];
  conditions: AnimalMedicalConditionModel[];
  quarantines: AnimalQuarantineModel[];
  files: MedicalFile[];
  today: string;
  dueAlerts?: number;
}): MedicalCounts {
  return {
    records: input.records.length,
    prophylaxis: latestTreatmentPerGroup(input.treatments).length,
    prophylaxisAlerts: input.dueAlerts ?? 0,
    conditions: activeConditions(input.conditions).length,
    quarantines: activeOrPlannedQuarantines(input.quarantines, input.today).length,
    attachments: input.files.length,
  };
}
